import Icon, { IconName } from '@features/ui-atoms/components/Icon';
import Button, { ButtonVariants } from '@features/ui-form/components/Button';
import theme from '@features/ui-layout/styles/theme';
import { styled } from '@mui/joy';
import { XOR } from '@root/src/utils/types/XOR';
import { ReactNode, useCallback } from 'react';

export enum ToastVariants {
  SUCCESS,
  ERROR,
  INFO,
  WARNING,
}

export type ToastProps = {
  onClose?: () => void;
  title: string;
  variant?: ToastVariants;
} & XOR<{ message?: string }, { children?: ReactNode }>;

type ToastifyProps = {
  closeToast?: () => void;
};

/**
 * Toast component
 * @param param0
 * @param {string} param0.title Title of the toast
 * @param {string} param0.message Message of the toast (if children is not provided)
 * @param {ReactNode} param0.children Children of the toast (if message is not provided)
 * @param {ToastVariants} param0.variant Variant of the toast (default: INFO)
 * @param {() => void} param0.onClose Callback when the toast is closed
 * @param {() => void} param0.closeToast To close the toast
 */
const Toast = ({
  title,
  message,
  children,
  variant = ToastVariants.INFO,
  onClose,
  // Currently unused, it's actually used in showToast
  closeToast,
}: ToastProps & ToastifyProps) => {
  const onCloseWrapper = useCallback(() => {
    if (onClose) {
      onClose();
    }
    if (closeToast) {
      closeToast();
    }
  }, [closeToast, onClose]);

  let toastIcon: IconName | null = null;
  switch (variant) {
    case ToastVariants.SUCCESS:
      toastIcon = IconName.checkCircle;
      break;
    case ToastVariants.ERROR:
      toastIcon = IconName.add;
      break;
  }

  return (
    <ToastContainer variant={variant}>
      <ToastHeader>
        {toastIcon && <Icon name={toastIcon} size={24} />}
        <ToastTitle>{title}</ToastTitle>

        {(onClose || closeToast) && (
          <Button
            label={<Icon name={IconName.close} size={18} />}
            onClick={onCloseWrapper}
            variant={ButtonVariants.TEXT}
          />
        )}
      </ToastHeader>

      <ToastContent>{message ?? children}</ToastContent>
    </ToastContainer>
  );
};

export default Toast;

type StyleDef = {
  [variant in ToastVariants]: {
    borderColor: string;
    textColor: string;
  };
};

const styles: StyleDef = {
  [ToastVariants.ERROR]: {
    borderColor: theme.palette.red[500],
    textColor: theme.palette.red[500],
  },
  [ToastVariants.INFO]: {
    borderColor: theme.palette.blue[400],
    textColor: theme.palette.blue[400],
  },
  [ToastVariants.SUCCESS]: {
    borderColor: theme.palette.green[500],
    textColor: theme.palette.green[600],
  },
  [ToastVariants.WARNING]: {
    borderColor: theme.palette.yellow[500],
    textColor: theme.palette.yellow[500],
  },
};

const ToastContainer = styled('div')<{ variant: ToastVariants }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;

  color: ${({ variant }) => styles[variant].textColor};
  background: ${props => props.theme.palette.white};
  z-index: var(--z-index-toast);
  border-radius: 0px;
`;

const ToastHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToastTitle = styled('h3')`
  margin-right: auto;
  flex: 1;
`;

const ToastContent = styled('div')(({ theme: { palette, typography } }) => ({
  ...typography['body-small'],
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  color: palette.grey5,
  // Validation errors arrive as one line per rejected field
  whiteSpace: 'pre-line',
}));
